<template>
  <div class="avance-panel">
    <div v-if="cargando" class="estado-msg">Cargando avance del proceso…</div>
    <div v-else-if="error" class="estado-msg error">{{ error }}</div>

    <template v-else-if="avance">
      <!-- ═══ Cabecera + KPIs globales ═══ -->
      <div class="cabecera">
        <div class="meta">
          <span><b>{{ avance.programa ?? 'Institucional' }}</b><template v-if="avance.campus"> · {{ avance.campus }}</template></span>
          <span>Periodo {{ avance.periodo }}</span>
          <span class="pill" :class="estadoClase(avance.estado)">{{ estadoTexto(avance.estado) }}</span>
        </div>
        <div class="acciones">
          <button class="btn" :disabled="exportando" @click="exportarExcelFn">Exportar Excel</button>
          <button class="btn" :disabled="exportando" @click="exportarCSVFn">Exportar CSV</button>
          <button class="btn primario" :disabled="exportando" @click="descargarPdf">{{ exportando ? 'Generando…' : 'Descargar PDF' }}</button>
        </div>
      </div>

      <div class="resumen-global">
        <div class="gp">
          <div class="gp-ring" :style="ringStyle(avance.global.pct_calificados)">
            <span>{{ fmtPct(avance.global.pct_calificados) }}</span>
          </div>
          <div class="gp-info">
            <span class="gp-lbl">Indicadores calificados</span>
            <span class="gp-sub">{{ avance.global.calificados }} de {{ avance.global.total_indicadores }} indicadores</span>
          </div>
        </div>
        <div class="gf-row">
          <div class="gf">
            <span class="gf-top"><span class="gf-lbl">Fichas documentales</span><span class="gf-num">{{ fmtPct(avance.global.pct_fichas.documental) }}</span></span>
            <div class="barra-fondo mini"><div class="barra-rel" :style="{ width: (avance.global.pct_fichas.documental ?? 0) + '%' }"></div></div>
          </div>
          <div class="gf">
            <span class="gf-top"><span class="gf-lbl">Fichas cuantitativas</span><span class="gf-num">{{ fmtPct(avance.global.pct_fichas.cuantitativa) }}</span></span>
            <div class="barra-fondo mini"><div class="barra-rel" :style="{ width: (avance.global.pct_fichas.cuantitativa ?? 0) + '%' }"></div></div>
          </div>
          <div class="gf">
            <span class="gf-top"><span class="gf-lbl">Fichas de opinión</span><span class="gf-num">{{ fmtPct(avance.global.pct_fichas.opinion) }}</span></span>
            <div class="barra-fondo mini"><div class="barra-rel" :style="{ width: (avance.global.pct_fichas.opinion ?? 0) + '%' }"></div></div>
          </div>
        </div>
      </div>

      <p class="nota">Generado {{ fmtFecha(avance.generado_en) }} · datos calculados al momento de la consulta.</p>

      <!-- ═══ Detalle por factor ═══ -->
      <div class="contenedor-tabla" v-drag-scroll>
        <table class="lista-tabla">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th>Factor</th>
              <th>Líder</th>
              <th class="ctr">% Calificados</th>
              <th class="ctr">% Doc.</th>
              <th class="ctr">% Cuant.</th>
              <th class="ctr">% Opin.</th>
              <th class="ctr">Semáforo documental</th>
              <th class="ctr">Pendientes</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(f, idx) in factoresOrdenados" :key="f.id_componente">
              <tr>
                <td class="ctr col-num">{{ f.ordinal ? formatOrdinal(f.ordinal) : (idx + 1) }}</td>
                <td>{{ f.nombre }}</td>
                <td>
                  <span v-if="f.lider">{{ f.lider.name }}</span>
                  <span v-else class="pill p-rojo">Sin líder</span>
                </td>
                <td class="ctr">
                  <div class="barra-fondo" :title="`${f.calificados}/${f.total_indicadores}`">
                    <div class="barra-rel" :style="{ width: (f.pct_calificados ?? 0) + '%' }"></div>
                  </div>
                  <span class="barra-txt">{{ fmtPct(f.pct_calificados) }}</span>
                </td>
                <td class="ctr">{{ fmtPct(f.pct_fichas.documental) }}</td>
                <td class="ctr">{{ fmtPct(f.pct_fichas.cuantitativa) }}</td>
                <td class="ctr">{{ fmtPct(f.pct_fichas.opinion) }}</td>
                <td class="ctr semaforo">
                  <span v-if="f.semaforo_documental.verde" class="pill p-verde">{{ f.semaforo_documental.verde }}</span>
                  <span v-if="f.semaforo_documental.amarillo" class="pill p-ambar">{{ f.semaforo_documental.amarillo }}</span>
                  <span v-if="f.semaforo_documental.rojo" class="pill p-rojo">{{ f.semaforo_documental.rojo }}</span>
                  <span v-if="sinSemaforo(f)" class="nota">—</span>
                </td>
                <td class="ctr">
                  <button
                    v-if="f.pendientes.length"
                    class="chip-pendientes"
                    @click="toggleExpandir(f.id_componente)"
                  >
                    {{ f.pendientes.length }} {{ expandidos.has(f.id_componente) ? '▲' : '▼' }}
                  </button>
                  <span v-else class="nota">0</span>
                </td>
              </tr>
              <tr v-if="expandidos.has(f.id_componente)" class="fila-pendientes">
                <td colspan="9">
                  <div class="pendientes-detalle">
                    <b>Indicadores pendientes de calificación</b>
                    <ul>
                      <li v-for="p in f.pendientes" :key="p.id_indicador">
                        <span class="caract">{{ p.caracteristica }}</span> — {{ p.enunciado }}
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="!factoresOrdenados.length">
              <td colspan="9" class="ctr nota">El proceso no tiene factores con estructura.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
/**
 * Panel de avance por factor.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch, onMounted } from 'vue'
import { getAvanceProcesoService, descargarPdfAvanceService, type AvanceProceso, type AvanceFactor } from '@/services/AvanceProcesoService'
import { exportarExcel, exportarCSV, type Celda } from '@/utils/exportUtils'

interface FactorConOrdinal extends AvanceFactor {
  ordinal?: string
}

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

const props = defineProps<{ idEjecucion: number }>()

const avance = ref<AvanceProceso | null>(null)
const cargando = ref(false)
const error = ref('')
const exportando = ref(false)
const expandidos = ref<Set<number>>(new Set())

const factoresOrdenados = computed((): FactorConOrdinal[] => {
  if (!avance.value) return []
  return [...avance.value.factores as FactorConOrdinal[]].sort((a, b) => {
    const oa = a.ordinal ?? ''
    const ob = b.ordinal ?? ''
    if (oa && ob) return oa.localeCompare(ob, undefined, { numeric: true })
    return (a.nombre ?? '').localeCompare(b.nombre ?? '', undefined, { numeric: true })
  })
})

const ESTADOS: Record<string, { texto: string; clase: string }> = {
  preparacion: { texto: 'Preparación', clase: 'p-gris' },
  ejecucion: { texto: 'Ejecución', clase: 'p-ambar' },
  evaluacion: { texto: 'Evaluación', clase: 'p-ambar' },
  informe: { texto: 'Informe', clase: 'p-verde' },
  finalizado: { texto: 'Finalizado', clase: 'p-verde' }
}

function estadoTexto(e: string): string {
  return ESTADOS[e]?.texto ?? e
}
function estadoClase(e: string): string {
  return ESTADOS[e]?.clase ?? 'p-gris'
}
function fmtPct(v: number | null): string {
  return v === null ? 'N/D' : `${v.toFixed(1)} %`
}
/** Dona de progreso (conic-gradient) para el % global calificado. */
function ringStyle(v: number | null): Record<string, string> {
  const grados = (v ?? 0) * 3.6
  return { background: `conic-gradient(#0e69a0 ${grados}deg, #e8f1f7 ${grados}deg)` }
}
function fmtFecha(iso: string): string {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString('es-CO')
}
function sinSemaforo(f: AvanceFactor): boolean {
  const s = f.semaforo_documental
  return !s.verde && !s.amarillo && !s.rojo
}
function toggleExpandir(id: number): void {
  const s = new Set(expandidos.value)
  s.has(id) ? s.delete(id) : s.add(id)
  expandidos.value = s
}

async function cargar(): Promise<void> {
  if (!props.idEjecucion) return
  cargando.value = true
  error.value = ''
  const r = await getAvanceProcesoService(props.idEjecucion)
  if (r.ok && r.data) {
    avance.value = r.data
  } else {
    error.value = r.error ?? 'No se pudo cargar el avance'
    avance.value = null
  }
  cargando.value = false
}

/** Aplana los factores a filas para las exportaciones tabulares. */
function filasExport(): Celda[][] {
  if (!avance.value) return []
  const pct = (v: number | null): Celda => (v === null ? 'N/D' : v)
  return avance.value.factores.map(f => [
    f.nombre,
    f.lider?.name ?? 'Sin líder',
    f.total_indicadores,
    f.calificados,
    pct(f.pct_calificados),
    pct(f.pct_fichas.documental),
    pct(f.pct_fichas.cuantitativa),
    pct(f.pct_fichas.opinion),
    f.semaforo_documental.verde,
    f.semaforo_documental.amarillo,
    f.semaforo_documental.rojo,
    f.pendientes.length
  ])
}

const HEADERS = [
  'Factor', 'Líder', 'Indicadores', 'Calificados', '% Calificados',
  '% Fichas documental', '% Fichas cuantitativa', '% Fichas opinión',
  'Doc. verde', 'Doc. amarillo', 'Doc. rojo', 'Pendientes'
]

function tabla() {
  return {
    nombre: `avance-proceso-${props.idEjecucion}`,
    titulo: 'Avance del proceso',
    headers: HEADERS,
    rows: filasExport()
  }
}

async function exportarExcelFn(): Promise<void> {
  await exportarExcel(tabla())
}
function exportarCSVFn(): void {
  exportarCSV(tabla())
}
async function descargarPdf(): Promise<void> {
  exportando.value = true
  await descargarPdfAvanceService(props.idEjecucion)
  exportando.value = false
}

onMounted(cargar)
watch(() => props.idEjecucion, cargar)
</script>

<style scoped>
.avance-panel { font-size: 0.9rem; color: var(--text-primary); font-family: var(--font-family); min-width: 0; overflow-x: hidden; }
.estado-msg { padding: 1.2rem; color: var(--text-secondary); }
.estado-msg.error { color: var(--color-error); }

.cabecera { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 0.6rem; margin-bottom: 0.9rem; min-width: 0; }
.meta { display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap; color: var(--text-secondary); min-width: 0; flex: 1 1 180px; }
.acciones { display: flex; gap: 0.4rem; flex-wrap: wrap; }
@media (max-width: 720px) {
  .cabecera { flex-direction: column; }
  .acciones { width: 100%; }
  .btn { flex: 1; text-align: center; justify-content: center; font-size: 0.78rem; padding: 0.35rem 0.5rem; }
}

.btn { border: 1px solid #0e69a0; background: #fff; color: #0e69a0; padding: 0.4rem 0.85rem; border-radius: var(--radius-md); cursor: pointer; font-size: 0.82rem; font-weight: 600; transition: all 0.15s ease; }
.btn:hover { background: #0e69a0; color: #fff; }
.btn.primario { background: #eea222; color: #222; border-color: #eea222; }
.btn.primario:hover { background: #0e69a0; color: #fff; border-color: #0e69a0; }
.btn:disabled { opacity: 0.55; cursor: default; }

/* Resumen global */
.resumen-global { display: grid; grid-template-columns: minmax(220px, 1fr) 2fr; gap: 1rem; margin-bottom: 0.7rem; }
@media (max-width: 860px) { .resumen-global { grid-template-columns: 1fr; } }
.gp { display: flex; align-items: center; gap: 0.9rem; background: #f5f9fc; border: 1px solid var(--border-separator); border-radius: var(--radius-md); padding: 0.9rem 1rem; min-width: 0; }
.gp-ring { width: 76px; height: 76px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gp-ring span { width: 56px; height: 56px; background: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; color: #0e69a0; font-size: 0.92rem; }
.gp-info { display: flex; flex-direction: column; min-width: 0; }
.gp-lbl { font-weight: 700; color: #333; }
.gp-sub { font-size: 0.78rem; color: var(--text-muted); margin-top: 0.2rem; }

.gf-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.7rem; }
.gf { background: #fff; border: 1px solid var(--border-separator); border-radius: var(--radius-md); padding: 0.7rem 0.85rem; display: flex; flex-direction: column; justify-content: center; min-width: 0; }
.gf-top { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.4rem; gap: 0.4rem; flex-wrap: wrap; }
.gf-lbl { font-size: 0.76rem; color: var(--text-tertiary); min-width: 0; }
.gf-num { font-size: 1.15rem; font-weight: 800; color: #0e69a0; flex-shrink: 0; }

.nota { font-size: 0.76rem; color: var(--text-muted); font-style: italic; margin: 0.2rem 0 0.8rem; }

/* Tabla */
.contenedor-tabla { overflow-x: auto; -webkit-overflow-scrolling: touch; min-width: 0; }
.lista-tabla { width: 100%; min-width: 680px; border-collapse: collapse; font-size: 0.82rem; }
.lista-tabla th { background: #0e69a0; color: #fff; padding: 0.5rem 0.6rem; text-align: left; font-weight: 600; white-space: nowrap; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.03em; border: 1px solid #0b587f; }
.lista-tabla td { border: 1px solid var(--border-separator); padding: 0.45rem 0.6rem; vertical-align: middle; }
.lista-tabla tbody tr:nth-child(odd):not(.fila-pendientes) td { background: #fafcfe; }
.ctr { text-align: center; }

.pill { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 0.72rem; font-weight: 700; white-space: nowrap; }
.p-verde { background: #d4edda; color: #155724; }
.p-ambar { background: #fff3cd; color: #856404; }
.p-rojo  { background: #f8d7da; color: #721c24; }
.p-gris  { background: #f0f0f0; color: #666; }
.semaforo .pill { margin: 0 1px; }

.barra-fondo { background: #e9eef2; border-radius: 5px; height: 9px; width: 100%; max-width: 100px; margin: 0 auto 3px; overflow: hidden; }
.barra-fondo.mini { max-width: none; height: 7px; margin: 0; }
.barra-rel { background: linear-gradient(90deg, #0e69a0, #2b9bd1); height: 100%; border-radius: 5px; }
.barra-txt { font-size: 0.74rem; color: var(--text-secondary); font-weight: 600; }

.col-num { width: 36px; min-width: 36px; text-align: center; font-weight: 700; color: #0e69a0; white-space: nowrap; }
.chip-pendientes { border: 1px solid #f5c6cb; background: #fdf1f2; color: #a12b36; border-radius: var(--radius-sm); padding: 0.15rem 0.5rem; cursor: pointer; font-size: 0.76rem; font-weight: 600; }
.chip-pendientes:hover { background: #f8d7da; }
.fila-pendientes td { background: #f5f9fc; }
.pendientes-detalle ul { margin: 0.3rem 0 0.1rem; padding-left: 1.1rem; }
.pendientes-detalle li { margin-bottom: 0.15rem; }
.caract { color: #0e69a0; font-weight: 600; }
</style>
